import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { CardDescription, CardTitle } from "design-system/molecules/Card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { SignupSchema } from "schema"
import { z } from "zod"

type FieldValues = z.infer<typeof SignupSchema>

type Props = {
  onSubmit: SubmitHandler<FieldValues>
  isPending: boolean
}

export function SignupForm({ onSubmit, isPending }: Props) {
  const { t, i18n } = useTranslation()
  const form = useForm<FieldValues>({
    resolver: zodResolver(SignupSchema),
    defaultValues: {
      name: "",
      email: "",
      role: "",
      locale: i18n.language === "es-ES" ? "es_ES" : "en_US",
    },
  })

  const companySize = ["10", "30", "50", "100", "150", "300", "500", "1000", "+1000"]

  return (
    <Form
      {...form}
      errorTranslations={getErrorTranslations()}
      onSubmit={form.handleSubmit(onSubmit)}
      className="w-full"
    >
      <div className="flex flex-col gap-4 mb-4 items-center text-xl">
        <CardTitle>{t("auth.signup.title")}</CardTitle>
        <CardDescription className="text-foreground">
          {t("auth.signup.description")}
        </CardDescription>
      </div>
      <div className="gap-4 flex flex-col w-full">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("auth.name.field")}</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  placeholder={t("auth.name.placeholder")}
                  autoComplete="name"
                  {...field}
                  className="bg-background/75"
                />
              </FormControl>
              <FormDescription />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("auth.corporateEmail.field")}</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder={t("auth.corporateEmail.placeholder")}
                  autoComplete="new-email" // Prevent this to be already filled
                  className="bg-background/75"
                  {...field}
                />
              </FormControl>
              <FormDescription />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("auth.role.field")}</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  placeholder={t("auth.role.placeholder")}
                  className="bg-background/75"
                  {...field}
                />
              </FormControl>
              <FormDescription />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="companySize"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("auth.companySize.field")}</FormLabel>
              <FormControl>
                <Select
                  {...field}
                  onValueChange={(ev) => {
                    field.onChange(ev)
                  }}
                >
                  <SelectTrigger className="bg-background/75">
                    <SelectValue placeholder={t("auth.companySize.placeholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {companySize.map((size) => (
                      <SelectItem key={size} value={size}>
                        {t(`auth.companySize.options.${size}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormDescription />
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="flex flex-col gap-4 pt-4">
        <Button disabled={isPending || !form.formState.isValid}>{t("auth.signup.submit")}</Button>
      </div>
    </Form>
  )
}
